import type { FormatId, HtmlPageKind } from '@app/formats'
import type { HtmlPageWidth } from '@app/shared'
import type { LocalFacesInput } from '@segnavia/format'
import type { PageInput } from '@segnavia/frame'
import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { type ComponentType, type ReactNode, useMemo } from 'react'
import { docHostClient } from '../../lib/doc-hosts'
import { Notice } from '../index'
import { HtmlDocView } from './HtmlDocView'
import { SlidesView } from './SlidesView'

export type DocRef = { host: string; spaceId: string; key: string }

export type DocFormatId = Extract<FormatId, 'html' | 'slides'>

export type DocExtra = {
  html: { kind: HtmlPageKind }
  slides: {
    slides: {
      id: string
      version: number
      name: string | null
      notes?: string
    }[]
    canWrite: boolean
  }
}

type DocViewBase<F extends DocFormatId> = {
  format: F
  title: string
  facts: {
    label: string
    value: ReactNode
    icon?: ComponentType<{ size?: number; 'aria-hidden'?: boolean }>
  }[]
  paper?: { widthMm: number; heightMm: number }
  orientation?: 'portrait' | 'landscape'
  /** A screen page's width; absent is the full window. */
  width?: HtmlPageWidth
  downloads?: { variant: string; label: string }[]
  extra: DocExtra[F]
}

export type LoadedDocView = {
  [F in DocFormatId]: DocViewBase<F>
}[DocFormatId]

export type PagePreview = {
  page: PageInput
  legacy: boolean
  unavailable: string[]
}

export type DocHostClient = {
  view(ref: DocRef): UseQueryOptions<LoadedDocView>
  previewTicket(ref: DocRef, part?: string): Promise<{ url: string }>
  previewPage?(ref: DocRef): Promise<PagePreview>
  download?(
    ref: DocRef,
    variant?: string,
  ): Promise<{ blob: Blob; filename: string }>
  updateSlideNotes?(
    ref: DocRef,
    slide: { id: string; version: number; notes: string | null },
  ): Promise<{ id: string; version: number; notes: string | null }>
  moveSlide?(
    ref: DocRef,
    slide: {
      id: string
      version: number
      after?: string
      before?: string
    },
  ): Promise<{ id: string; version: number }>
  /** Present only where the server prints this host's pages with the reader's
   *  own faces; anywhere else a download prints the fallbacks. */
  printWithLocalFaces?(
    ref: DocRef,
    faces: LocalFacesInput,
    fallbackFilename: string,
  ): Promise<{ blob: Blob; filename: string }>
}

type DocFormatViewProps<F extends DocFormatId = DocFormatId> = {
  doc: DocRef
  client: DocHostClient
  loaded: Extract<LoadedDocView, { format: F }>
  /** Whether the document is the whole page, so its facts wait to be asked
   *  for rather than standing above it. See `DocFacts`. */
  factsOnDemand: boolean
}

/**
 * Every document-backed format has one complete presentation.
 *
 * `DocFormatId` widens with the shared registry, so adding a doc-backed format
 * fails this record until the browser has a view for it.
 */
export const DOC_VIEWS: Record<
  DocFormatId,
  ComponentType<DocFormatViewProps>
> = {
  html: ({ loaded, ...props }) =>
    loaded.format === 'html' ? (
      <HtmlDocView {...props} loaded={loaded} />
    ) : null,
  slides: ({ loaded, ...props }) =>
    loaded.format === 'slides' ? (
      <SlidesView {...props} loaded={loaded} />
    ) : null,
}

/** Resolve a hosted document, then hand its complete frame to its format. */
export function DocView({
  doc: given,
  factsOnDemand = false,
}: {
  doc: DocRef
  factsOnDemand?: boolean
}) {
  /* One document is one reference, however often the caller rebuilds the
     object: a view keyed on it would otherwise tear down its frames on every
     render, and a frame's ticket loads only once. */
  const doc = useMemo(
    () => ({ host: given.host, spaceId: given.spaceId, key: given.key }),
    [given.host, given.spaceId, given.key],
  )
  const client = docHostClient(doc.host)
  const view = useQuery(client.view(doc))

  if (view.error) return <Notice tone="error">{view.error.message}</Notice>
  if (!view.data) return <Notice>Preparing page…</Notice>

  const View = DOC_VIEWS[view.data.format]
  return (
    <View
      doc={doc}
      client={client}
      loaded={view.data}
      factsOnDemand={factsOnDemand}
    />
  )
}
